import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchMetrics,
  createMetric,
  updateMetric,
  deleteMetric,
} from "@/store/metricsSlice";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchField } from "@/components/SearchField";
import { ResourceFormFields } from "@/components/ResourceFormFields";
import type { Metric, ResourceFormValues } from "@/types";
import { EMPTY_RESOURCE_FORM } from "@/constants/forms";

export function Metrics() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const getErrorMessage = useApiErrorMessage();
  const { items, loading } = useAppSelector((s) => s.metrics);
  const { notify } = useNotification();

  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Metric | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Metric | null>(null);

  const { control, handleSubmit, reset } = useForm<ResourceFormValues>({
    defaultValues: EMPTY_RESOURCE_FORM,
  });

  useEffect(() => {
    dispatch(fetchMetrics());
  }, [dispatch]);

  const filtered = items.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()),
  );

  const openCreate = () => {
    setEditing(null);
    reset(EMPTY_RESOURCE_FORM);
    setDialogOpen(true);
  };

  const openEdit = (metric: Metric) => {
    setEditing(metric);
    reset({
      name: metric.name,
      shortName: metric.shortName || "",
      displayName: metric.displayName || "",
      slug: metric.slug || "",
      description: metric.description || "",
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: ResourceFormValues) => {
    try {
      if (editing) {
        await dispatch(updateMetric({ id: editing.id, data })).unwrap();
        notify(t("metrics.updated"), "success");
      } else {
        await dispatch(createMetric(data)).unwrap();
        notify(t("metrics.created"), "success");
      }
      setDialogOpen(false);
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteMetric(deleteTarget.id)).unwrap();
      notify(t("metrics.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  return (
    <Box>
      <PageHeader
        title={t("metrics.title")}
        subtitle={t("metrics.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            {t("metrics.create")}
          </Button>
        }
      />
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t("common.search")}
      />
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("common.loading")}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {search ? t("common.noResults") : t("metrics.noMetrics")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((metric, idx) => (
                <ListItem
                  key={metric.id}
                  divider={idx < filtered.length - 1}
                  sx={{ px: 3, py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {metric.name}
                        </Typography>
                        {metric.shortName && (
                          <Chip
                            label={metric.shortName}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={metric.description || metric.slug}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEdit(metric)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(metric)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editing ? t("metrics.edit") : t("metrics.create")}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: "8px !important",
            }}
          >
            <ResourceFormFields control={control} ns="metrics" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {editing ? t("common.save") : t("common.create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("metrics.delete")}
        message={t("metrics.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
